import { EMPTY_DISPLAY_VALUE } from '../../../constants/formatting';
import type { OmitStrict } from '../../../types';
import { formatNumber, isNullish } from '../../../utils';
import DataTableCell from '../primitives/DataTableCell';
import { DataTableCellPrimitiveProps } from '../primitives/DataTableCell/DataTableCell';

const getDisplayValue = (value: unknown) => {
  if (typeof value === 'number') {
    return formatNumber(value, { fallback: EMPTY_DISPLAY_VALUE });
  }

  if (isNullish(value)) {
    return EMPTY_DISPLAY_VALUE;
  }

  return value;
};

export type DefaultDataTableCellProps<TData = Record<string, unknown>> =
  OmitStrict<DataTableCellPrimitiveProps, 'columnKey'> & {
    /** You can use this value if you wanna render custom value (otherwise data[columnKey] will be displayed)  */
    label?: string | number;
    data?: TData;
    columnKey: keyof TData | (string & {});
  };

/**
 * Default implementation of body cell component
 */
const DefaultDataTableCell = <TData extends Record<string, unknown>>(
  props: DefaultDataTableCellProps<TData>,
): JSX.Element => {
  const { data, label, columnKey, ...rest } = props;

  const value = label ?? data?.[columnKey];

  const displayValue = getDisplayValue(value);

  return (
    <DataTableCell
      fontFamily={
        typeof value === 'number' && displayValue !== EMPTY_DISPLAY_VALUE
          ? 'numeric'
          : undefined
      }
      {...rest}
      columnKey={columnKey.toString()}>
      {displayValue}
    </DataTableCell>
  );
};

export default DefaultDataTableCell;
